import { useQuery } from '@tanstack/react-query';

import { getRuleAuthorities } from '@/api/rules';
import { useAuth } from '../auth/AuthContext';

/** Deterministic "Governing authority" line for a finding's rule (design §7.2).
 *  High trust — no model in the loop; sourced from the rule's curated references
 *  materialized into `rule_authorities`. Renders nothing until it has data. */
export function GoverningAuthority({ ruleId }: { ruleId: string }) {
  const { isSignedIn, userKey, getIdToken } = useAuth();

  const authoritiesQuery = useQuery({
    queryKey: ['rule-authorities', userKey, ruleId],
    enabled: isSignedIn && ruleId.length > 0,
    queryFn: async () => getRuleAuthorities(ruleId, (await getIdToken()) ?? ''),
  });

  // Distinguish a real load failure from "this rule has no mapped authority":
  // rendering the same nothing for both would hide a network/500 error from the
  // practitioner (the page's other queries surface their errors the same way).
  if (authoritiesQuery.isError) {
    return (
      <section className="grid gap-1.5 rounded-md border border-border bg-muted/30 px-3 py-2">
        <p className="text-xs uppercase tracking-wide text-brand-slate">Governing authority</p>
        <p className="text-sm text-brand-slate">Couldn't load the governing authority.</p>
      </section>
    );
  }

  const items = authoritiesQuery.data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <section className="grid gap-1.5 rounded-md border border-border bg-muted/30 px-3 py-2">
      <p className="text-xs uppercase tracking-wide text-brand-slate">Governing authority</p>
      <ul className="grid gap-1">
        {items.map((a) => (
          <li key={a.citation} className="text-sm text-brand-navy">
            <span className="font-medium">{a.citation}</span>
            {a.description ? (
              <span className="text-brand-slate"> — {a.description}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
